import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import { readQuery } from '../query';
import ListTools from '../components/ListTools';
import ActiveFilters from '../components/ActiveFilters';
import EmptyRow from '../components/EmptyRow';
import Guid from '../components/Guid';
import Pager from '../components/Pager';

/**
 * Emails — every confirmation email the system received or sent.
 *
 * The list this page replaces is how someone finds the email behind a booking
 * that went wrong, so all nine of its filters are here: three in the row above
 * the fold, as the design draws it, and the six rarer ones behind "More
 * filters". Nothing is dropped — a filter that vanishes is a search somebody can
 * no longer run.
 *
 * From and To share one column, again from the design. They are read together —
 * "who wrote to whom" — and apart they cost two thirds of the width of a table
 * that also has to show a subject.
 *
 * **Sorting is offered on the four columns the endpoint can actually sort by.**
 * `ConfirmationEmailController.Search` switches on `id`, `from` and `to` and
 * falls through to CreatedDate for everything else, so the old page's arrow on
 * Subject sorts by date while claiming to sort by subject. A control that lies
 * about what it did is worse than one that is not there.
 */

const SORTABLE = { Id: 'id', From: 'from', To: 'to', CreatedDate: 'createdDate' };

const BASE_FILTERS = [
  { name: 'id', label: 'Email id', placeholder: 'Find by id' },
  { name: 'signupId', label: 'Signup id', placeholder: 'Find by signup id' },
  { name: 'subject', label: 'Subject', placeholder: 'Text in the subject' }
];

const MORE_FILTERS = [
  { name: 'sender', label: 'Sender', placeholder: 'Find by sender' },
  { name: 'seasonalCourtPurchaseId', label: 'Seasonal court purchase id', placeholder: 'Find by id' },
  { name: 'createdFrom', label: 'Created from', type: 'datetime-local' },
  { name: 'createdTo', label: 'Created to', type: 'datetime-local' }
];

// The API matches this against `HasSignup.Contains("without")`, so the values
// are the strings it expects, not a boolean.
const SIGNUP_OPTIONS = [
  { value: 'With signups', label: 'With a signup' },
  { value: 'Without signups', label: 'Without a signup' }
];

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

// Excel opens a comma-separated file in one column outside en-US locales, hence
// the semicolon; quotes are doubled and the whole field wrapped, so a subject
// containing either survives the round trip.

const EXPORT_COLUMNS = [
  ['id', 'Id'],
  ['subject', 'Subject'],
  ['from', 'From'],
  ['to', 'To'],
  ['createdDateCet', 'Created (CET)'],
  ['status', 'Status'],
  ['signupId', 'Signup id']
];

const FILTER_FIELDS = [...BASE_FILTERS, ...MORE_FILTERS];

const EmailsInternal = ({ api }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => readQuery(location.search), [location.search]);

  const [rows, setRows] = useState(null);
  const [pages, setPages] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(query);
  const [showMore, setShowMore] = useState(false);

  const page = parseInt(query.page, 10) || 1;
  // The same question the chips answer, so the empty row and the chips
  // can never disagree about whether anything is filtering the list.
  const filtered = FILTER_FIELDS.some(({ name: key }) => (
    key !== 'page' && key !== 'resultsPerPage' && query[key] !== undefined && query[key] !== ''
  ));

  const perPage = parseInt(query.resultsPerPage, 10) || 15;
  const orderBy = query.orderBy || 'createdDate';
  const orderDirection = query.orderDirection === 'asc' ? 'asc' : 'desc';

  useEffect(() => {
    setDraft(query);
  }, [query]);

  const load = useCallback(async () => {
    setRows(null);
    setFailed(false);
    try {
      // Answers `{ confirmations, pages, currentPage, orderBy, orderDirection }`.
      const data = await api.confirmationEmail.searchConfirmations({ filters: query });
      setRows(data?.confirmations ?? []);
      setPages(typeof data?.pages === 'number' ? data.pages : null);
    } catch (error) {
      setFailed(true);
    }
  }, [api, query]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (next, { resetPage = true } = {}) => {
    // The sort survives a search. It is not a filter — the filter row does not
    // hold it and `next` is built from that row — so applying one used to drop
    // the column somebody had just sorted by, silently, back to the default.
    const merged = {
      orderBy: query.orderBy,
      orderDirection: query.orderDirection,
      ...next
    };
    if (resetPage) delete merged.page;

    const search = Object.entries(merged)
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    navigate(search ? `?${search}` : '', { replace: false });
  };

  const set = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));

  // Clicking the sorted column flips the direction; a different column starts at
  // newest-first, which is what you want on every column here.
  const sortBy = key => apply({
    ...query,
    orderBy: key,
    orderDirection: orderBy.toLowerCase() === key.toLowerCase() && orderDirection === 'desc' ? 'asc' : 'desc'
  });

  const sortMark = key =>
    (orderBy.toLowerCase() === key.toLowerCase() ? (orderDirection === 'desc' ? ' ↓' : ' ↑') : '');

  const header = (label, key, className) => (
    <th className={className}>
      {key ? (
        <button type="button" className="next-th-sort" onClick={() => sortBy(key)}>
          {label}{sortMark(key)}
        </button>
      ) : label}
    </th>
  );

  const activeMoreCount = MORE_FILTERS.filter(({ name }) => query[name]).length
    + (query.hasSignup ? 1 : 0);

  const field = ({ name, label, placeholder, type }) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        type={type || 'text'}
        value={draft[name] ?? ''}
        placeholder={placeholder}
        onChange={event => set(name, event.target.value)}
      />
    </label>
  );

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Emails</h1>
          <p className="next-muted">
            {rows === null
              ? 'Loading…'
              : `Page ${page}${pages ? ` of ${pages.toLocaleString()}` : ''} · ${perPage} per page`}
          </p>
        </div>
        <div className="next-page__actions">
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <section className="next-card">
        <form
          className="next-filters"
          onSubmit={event => { event.preventDefault(); apply(draft); }}
        >
          <div className="next-filters__row">
            {BASE_FILTERS.map(field)}

            <div className="next-filters__buttons">
              <button type="submit" className="next-btn next-btn--primary">Search</button>
              <button
                type="button"
                className="next-btn"
                onClick={() => setShowMore(value => !value)}
              >
                {showMore ? 'Fewer filters' : 'More filters'}
                {activeMoreCount > 0 && <span className="next-badge">{activeMoreCount}</span>}
              </button>
            </div>
          </div>

          {showMore && (
            <div className="next-filters__row next-filters__row--more">
              {MORE_FILTERS.map(field)}

              <label className="next-filter">
                <span className="next-filter__label">Signup</span>
                <select
                  className="next-input"
                  value={draft.hasSignup ?? ''}
                  onChange={event => set('hasSignup', event.target.value)}
                >
                  <option value="">Any</option>
                  {SIGNUP_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>

              <div className="next-filters__buttons">
                <button
                  type="button"
                  className="next-btn next-btn--quiet"
                  onClick={() => { setDraft({}); apply({}); }}
                >
                  Clear all
                </button>
              </div>
            </div>
          )}
        </form>
      </section>

      <ActiveFilters
        fields={FILTER_FIELDS}
        query={query}
        onChange={next => { setDraft(next); apply(next); }}
      />

      <ListTools
        screenPath="/next/emails"
        search={location.search}
        columns={EXPORT_COLUMNS}
        rows={rows}
        exportName="confirmation-emails"
        perPage={perPage}
        fetchPage={next => api.confirmationEmail
          .searchConfirmations({ filters: { ...query, page: next } })
          .then(data => data?.confirmations ?? [])}
      />

      {failed ? (
        <div className="next-note next-note--error">
          Could not load the emails.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  {header('Id', SORTABLE.Id)}
                  {/* One column, but both of its fields can be sorted on and the
                      old page let you sort by either — so both stay clickable. */}
                  <th>
                    <button type="button" className="next-th-sort" onClick={() => sortBy(SORTABLE.From)}>
                      From{sortMark(SORTABLE.From)}
                    </button>
                    <span className="next-mail__arrow" aria-hidden> → </span>
                    <button type="button" className="next-th-sort" onClick={() => sortBy(SORTABLE.To)}>
                      To{sortMark(SORTABLE.To)}
                    </button>
                  </th>
                  {/* Subject carries no sort: the endpoint has no case for it. */}
                  {header('Subject')}
                  {header('Signup')}
                  {header('Created (CET)', SORTABLE.CreatedDate)}
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={5} className="next-muted next-table__note">Loading…</td></tr>
                )}
                {rows !== null && rows.length === 0 && (
                  <EmptyRow
                    columns={5}
                    noun="email"
                    filtered={filtered}
                    onClear={() => { setDraft({}); apply({}); }}
                  />
                )}
                {(rows ?? []).map(row => (
                  <tr key={row.id}>
                    <td data-label=""><Guid value={row.id} to={`/next/email/${row.id}`} /></td>
                    <td data-label="From → To">
                      <span className="next-mail__pair">
                        <span className="next-mail__from">{row.from || '—'}</span>
                        <span className="next-mail__arrow" aria-hidden>→</span>
                        {/* `to` is a comma-separated list; one recipient per line. */}
                        <span className="next-mail__to">
                          {String(row.to || '').split(',').map(value => value.trim()).filter(Boolean)
                            .map(value => <span key={value}>{value}</span>)}
                        </span>
                      </span>
                    </td>
                    <td data-label="Subject">{row.subject || '—'}</td>
                    <td data-label="Signup">
                      {row.signupId
                        ? <Guid value={row.signupId} to={`/next/signup/${row.signupId}`} />
                        : <span className="next-muted">—</span>}
                    </td>
                    <td className="next-nowrap next-muted" data-label="Created (CET)">
                      {fmtDate(row.createdDateCet || row.createdDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            perPage={perPage}
            onPerPageChange={size => apply({ ...query, resultsPerPage: size })}
            page={page}
            pages={pages}
            hasNext={pages ? page < pages : (rows ?? []).length >= perPage}
            onChange={next => apply({ ...query, page: next }, { resetPage: false })}
          />
        </section>
      )}
    </div>
  );
};

EmailsInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Emails = withApi(EmailsInternal);

Emails.requiresLogin = true;

export default Emails;
